import React, { lazy, Suspense, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import HeroSection from '@/components/HeroSection';
import PricingCalculator from '@/components/PricingCalculator';
import Footer from '@/components/Footer';
import LazySection from '@/components/LazySection';
import { usePageContent } from '@/hooks/usePageContent';
import { websiteSchema } from '@/data/businessSchema';
import { injectLocalBusinessSchema } from '@/utils/schema-helpers';

const seoDefaults = {
  title: 'Managed IT Services Canada | Cybersecurity & IT Support | Click One MSP',
  description: 'Click One MSP provides managed IT services across Canada. Proactive IT support, cybersecurity, 24/7 monitoring — zero onboarding fees. Flat-rate pricing for SMBs.',
  keywords: 'managed IT services, IT support Vancouver, cybersecurity, MSP, managed services provider, help desk, Microsoft 365, IT services Canada',
  og_title: 'Managed IT Services Canada | Click One MSP',
  og_description: 'Proactive IT management, enterprise-grade cybersecurity, and 24/7 help desk support for small and medium businesses — one flat monthly rate.',
};

const HomePage = () => {
  const { content: c } = usePageContent('home', 'home', { seo: seoDefaults });
  const seo = c.seo || seoDefaults;

  // Inject LocalBusiness JSON-LD via DOM (richer schema from schema-helpers).
  // Runs once on mount; cleans up its own <script data-schema="localbusiness">.
  useEffect(() => {
    injectLocalBusinessSchema();
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] overflow-x-hidden" data-page>
      <Helmet>
        <title>{seo.title || seoDefaults.title}</title>
        <meta name="description" content={seo.description || seoDefaults.description} />
        <meta name="keywords" content={seo.keywords || seoDefaults.keywords} />
        <meta property="og:title" content={seo.og_title || seo.title || seoDefaults.og_title} />
        <meta property="og:description" content={seo.og_description || seo.description || seoDefaults.og_description} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://clickonemsp.ca" />
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      </Helmet>
      <Navbar />
      <main>
        <HeroSection />
        <LazySection importFn={() => import('@/components/AboutSection')} fallbackHeight="600px" />
        <LazySection importFn={() => import('@/components/WhyChooseUs')} fallbackHeight="700px" />
        <LazySection importFn={() => import('@/components/ThreatDashboard')} fallbackHeight="500px" />
        <LazySection importFn={() => import('@/components/ServicesExplorer')} fallbackHeight="600px" />
        <LazySection importFn={() => import('@/components/AreasWeServe')} fallbackHeight="700px" />
        <LazySection importFn={() => import('@/components/HealthCheckQuiz')} fallbackHeight="500px" id="health-check" />
        <PricingCalculator />
        <LazySection importFn={() => import('@/components/Testimonials')} fallbackHeight="400px" />
        <LazySection importFn={() => import('@/components/LatestFromBlog')} fallbackHeight="600px" />
        <LazySection importFn={() => import('@/components/HomepageFAQ')} fallbackHeight="800px" />
        <LazySection importFn={() => import('@/components/OnboardingProcess')} fallbackHeight="500px" />
        <LazySection importFn={() => import('@/components/ContactSection')} fallbackHeight="600px" id="contact" />
      </main>
      <Footer />
    </div>
  );
};

export default HomePage;
